import {
  CalendarDateTime,
  getLocalTimeZone,
  toZoned,
} from "@internationalized/date";
import { differenceInMinutes, format } from "date-fns";
import { Duration } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/responseSchema";
import { getDisplayDuration } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/utils";
import { calendarDateTimeToDate } from "shared/utils/dateUtils";

export const toTimeTitleByType: { [key in Duration["type"]]: string } = {
  OPEN_ENDED: "Tidshorisont",
  TIMED: "Sluttidspunkt",
  PERIODIC: "Sluttidspunkt",
};

export const toTimeTitle = (type: keyof typeof toTimeTitleByType) =>
  toTimeTitleByType[type];

export const formatCalendarDateTime = (
  cdt: CalendarDateTime | null | undefined,
) => {
  if (!cdt) return "";
  const date = toZoned(cdt, getLocalTimeZone()).toDate();
  return format(date, " dd.MM.yyyy, HH:mm");
};

export type StartEndData = {
  start: Date | null;
  end: Date | null;
  openEndedMinutes?: number;
  title: string;
};

export const getStartEndData = (
  durations: Duration | null | undefined,
): StartEndData => {
  if (!durations) {
    return {
      start: null,
      end: null,
      openEndedMinutes: undefined,
      title: "",
    };
  }

  try {
    const dd = getDisplayDuration(durations);
    const start = dd?.fromTime ? calendarDateTimeToDate(dd.fromTime) : null;
    const end = dd?.toTime ? calendarDateTimeToDate(dd.toTime) : null;

    return {
      start,
      end,
      openEndedMinutes:
        durations.type === "OPEN_ENDED"
          ? Number(durations.extension_time_in_minutes ?? 0)
          : undefined,
      title: toTimeTitle(durations.type),
    };
  } catch {
    return {
      start: null,
      end: null,
      openEndedMinutes: undefined,
      title: toTimeTitle(durations.type),
    };
  }
};

export const getStartEndStrings = (durations: Duration) => {
  const dd = getDisplayDuration(durations);
  const startTime = formatCalendarDateTime(dd?.fromTime);
  const endTime =
    durations.type === "OPEN_ENDED"
      ? `${durations.extension_time_in_minutes} minutter`
      : formatCalendarDateTime(dd?.toTime);
  const title = toTimeTitle(durations.type);
  return { startTime, endTime, title };
};

export const splitDateAndTime = (
  value: string,
): { date: string; time: string } | null => {
  const parts = value
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);

  if (parts.length !== 2) return null;
  const [date, time] = parts;
  return { date, time };
};

export const getEventDurationInHours = (duration: Duration): number => {
  switch (duration.type) {
    case "TIMED": {
      const minutes = differenceInMinutes(
        calendarDateTimeToDate(duration.to_time),
        calendarDateTimeToDate(duration.from_time),
      );
      return minutes / 60;
    }
    case "OPEN_ENDED":
      return Number(duration.extension_time_in_minutes) / 60;
    case "PERIODIC": {
      const totalMinutes = duration.periods.reduce(
        (sum, period) =>
          sum +
          differenceInMinutes(
            calendarDateTimeToDate(period.to_time),
            calendarDateTimeToDate(period.from_time),
          ),
        0,
      );
      return totalMinutes / 60;
    }
  }
};
